import { z } from 'zod'
import axios from 'axios'
import { useMutation } from '@tanstack/react-query'
import fallbackPlaceholder from '../assets/fallbackPlaceholder.svg'

async function createPostComment(createPostComment: CreatePostComment) {
  const validatedData = CreatePostCommentSchema.parse(createPostComment)
  const response = await axios.post('/api/social/comments', {
    kind: validatedData.kind,
    post_id: validatedData.post_id,
    text: validatedData.text,
  })
  return response.data
}

export function useCreatePostComment() {
  return useMutation({
    mutationFn: createPostComment,
  })
}

async function createPostReaction(createPostReaction: CreatePostReaction) {
  const validatedData = CreatePostReactionSchema.parse(createPostReaction)
  const response = await axios.post('/api/social/reactions', {
    kind: validatedData.kind,
    post_id: validatedData.post_id,
    reaction_type: validatedData.reaction_type,
  })
  return response.data
}

export function useCreatePostReaction() {
  return useMutation({
    mutationFn: createPostReaction,
  })
}

export const ReactionsSchema = z.object({
  created_at: z.string(),
  player_id: z.number().nullable(),
  reaction_id: z.number(),
  reaction_type: z.string(),
  user_id: z.number(),
  user_first_name: z.string(),
  user_last_name: z.string(),
  user_photo: z
    .string()
    .url()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
})

export const ReactionsTypeSchema = z
  .object({
    red_heart: z.number(),
    thumbs_up: z.number(),
    partying_face: z.number(),
    rocket: z.number(),
  })
  .partial()

const ReactionEnum = z.enum(['thumbs_up', 'partying_face', 'rocket'])

const CreatePostReactionSchema = z.object({
  kind: z.string(),
  post_id: z.number(),
  reaction_type: ReactionEnum,
})

export type Reaction = z.infer<typeof ReactionEnum>
export type ReactionsType = z.infer<typeof ReactionsTypeSchema>
type CreatePostReaction = z.infer<typeof CreatePostReactionSchema>

export const CommentsSchema = z.object({
  comment_id: z.number(),
  created_at: z.string(),
  text: z.string(),
  user_id: z.number(),
  user_first_name: z.string(),
  user_last_name: z.string(),
  user_photo: z.string().nullable(),
  player_id: z.number().nullable(),
  reply_to_id: z.number().nullable(),
  reactions: z.array(ReactionsSchema),
})

const CreatePostCommentSchema = z.object({
  kind: z.string(),
  post_id: z.number(),
  text: z.string(),
})

export type Reactions = z.infer<typeof ReactionsSchema>
export type Comments = z.infer<typeof CommentsSchema>
type CreatePostComment = z.infer<typeof CreatePostCommentSchema>
